import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MiniTaskDialogue, { isMiniTaskDialogue } from './components/MiniTaskDialogue';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import SouthRoundedIcon from '@mui/icons-material/SouthRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { getStudentAvatar } from '../shared/studentAvatar';
import { fetchBoard } from './miniTasksApi';
import { readStoredBoardSession, writeStoredBoardSession } from './sessionStorage';
import { playMiniTaskSound, prepareMiniTaskSound, readMiniTaskSoundEnabled } from './soundEffects';
import {
  advanceLocalTournament, answerLocalMatch, createLocalMatch, elapsedLocalMs, matchDurationMs,
  localMatchStorageKey, localTournamentStorageKey, preloadTournamentImages,
  questionForSide, startLocalTournamentMatch, tickLocalMatch,
} from './tournamentLocalMatch';
import {
  abandonTournament, answerTournamentQuestion, createTournament, finishTournament, finishTournamentMatch,
  getCurrentTournament, getTournamentContents, startTournamentMatch,
} from './tournamentApi';

const violet = '#5b21b6';
const ink = '#242061';
const tournamentSoundPan = { a: -0.75, b: 0.75 };
const celebrationSoundDelayMs = 420;
const cardShadow = '0 6px 0 rgba(39,22,108,0.09), 0 15px 28px rgba(73,40,128,0.08)';
const actionButton = {
  minHeight: { xs: 58, md: 68 },
  px: { xs: 3, md: 5 },
  borderRadius: 99,
  bgcolor: '#7c3aed',
  color: '#fff',
  border: '2px solid rgba(255,255,255,0.9)',
  boxShadow: '0 5px 0 #4c1d95, 0 13px 25px rgba(91,33,182,0.23)',
  textTransform: 'none',
  fontWeight: 1000,
  fontSize: { xs: 19, md: 26 },
  '&:hover': { bgcolor: '#6d28d9', transform: 'translateY(-2px)', boxShadow: '0 7px 0 #4c1d95, 0 16px 28px rgba(91,33,182,0.25)' },
  '&:disabled': { bgcolor: '#cbd5e1', color: '#64748b', boxShadow: 'none' },
  transition: 'transform 160ms ease, box-shadow 160ms ease',
};

function roundTitle(round, lastRound) {
  if (round === lastRound) return 'Büyük Final';
  if (round === lastRound - 1 && lastRound > 2) return 'Yarı Final';
  if (round === lastRound - 2 && lastRound >= 4) return 'Çeyrek Final';
  return `${round}. Tur`;
}

function activityLabel(activity) {
  if (!activity?.label) return '';
  const label = {
    Vocabulary: 'Kelime Bilgisi',
    'Bilgi-Avı': 'Bilgi Avı',
  }[activity.label] || activity.label;
  return activity.lesson_name ? `${activity.lesson_name} · ${label}` : label;
}

function sourceMatchLabel(match, side, lastRound) {
  const sourceRound = match.round - 1;
  if (sourceRound < 1) return 'Eşleşmesi belirlenecek';
  const sourceNumber = (match.slot * 2) + (side === 'a' ? 1 : 2);
  const sourceRoundTitle = roundTitle(sourceRound, lastRound);
  return sourceRound === 1
    ? `${sourceNumber}. maç galibi`
    : `${sourceRoundTitle} · ${sourceNumber}. maç galibi`;
}

function winnerSoundPan(match, winner) {
  const winnerSide = ['a', 'b'].find((side) => String(match?.[`student_${side}`]?.id) === String(winner?.id));
  return tournamentSoundPan[winnerSide] || 0;
}

function useCelebrationSound(soundKey, kind, pan = 0) {
  useEffect(() => {
    if (!soundKey) return undefined;
    const storageKey = `mini-task-tournament-celebration:${soundKey}`;
    try {
      if (window.sessionStorage.getItem(storageKey) === '1') return undefined;
    } catch (_err) {
      // Session storage is optional; the sound can still play for this view.
    }
    const timeout = window.setTimeout(() => {
      playMiniTaskSound(kind, readMiniTaskSoundEnabled(), { pan });
      try {
        window.sessionStorage.setItem(storageKey, '1');
      } catch (_err) {
        // Ignore storage errors after playback.
      }
    }, celebrationSoundDelayMs);
    return () => window.clearTimeout(timeout);
  }, [kind, pan, soundKey]);
}

function StudentAvatar({ student, size = 62 }) {
  const theme = getStudentAvatar(student?.id, student?.avatar_id, student?.starter_emoji);
  return (
    <Box
      aria-hidden="true"
      sx={{
        position: 'relative', width: size, height: size, flex: '0 0 auto',
        borderRadius: '50%', bgcolor: '#fff', border: '3px solid #fff',
        boxShadow: 'inset 0 -5px 0 rgba(15,23,42,0.06), 0 5px 12px rgba(15,23,42,0.13)',
        display: 'grid', placeItems: 'center', overflow: 'hidden',
      }}
    >
      <Box sx={{ position: 'absolute', inset: 6, borderRadius: '50%', bgcolor: theme.bgColor }} />
      <Box sx={{ position: 'absolute', bottom: 2, width: '62%', height: '17%', borderRadius: '50% 50% 0 0', bgcolor: '#84cc16' }} />
      {theme.imageUrl ? <Box component="img" src={theme.imageUrl} alt="" loading="lazy" decoding="async"
        sx={{ position: 'relative', width: '92%', height: '92%', objectFit: 'contain' }} />
        : <Box component="span" sx={{ position: 'relative', fontSize: size * 0.6, lineHeight: 1, filter: 'drop-shadow(0 3px 0 rgba(15,23,42,0.12))' }}>{theme.avatar}</Box>}
    </Box>
  );
}

function PandaMark() {
  return (
    <Box sx={{ position: 'relative', width: { xs: 62, md: 88 }, height: { xs: 58, md: 82 }, flex: '0 0 auto' }}>
      <Box sx={{ position: 'absolute', left: 6, top: 2, width: 20, height: 20, borderRadius: '50%', bgcolor: '#111827' }} />
      <Box sx={{ position: 'absolute', right: 8, top: 2, width: 20, height: 20, borderRadius: '50%', bgcolor: '#111827' }} />
      <Box sx={{ position: 'absolute', left: 6, top: 8, width: { xs: 48, md: 68 }, height: { xs: 48, md: 68 },
        borderRadius: '50%', bgcolor: '#fff', boxShadow: '0 4px 0 rgba(15,23,42,0.18)' }}>
        <Box sx={{ position: 'absolute', left: '17%', top: '27%', width: '26%', height: '30%', borderRadius: '50%', bgcolor: '#111827', transform: 'rotate(-18deg)' }} />
        <Box sx={{ position: 'absolute', right: '17%', top: '27%', width: '26%', height: '30%', borderRadius: '50%', bgcolor: '#111827', transform: 'rotate(18deg)' }} />
        <Box sx={{ position: 'absolute', left: '28%', top: '36%', width: 6, height: 6, borderRadius: '50%', bgcolor: '#fff' }} />
        <Box sx={{ position: 'absolute', right: '28%', top: '36%', width: 6, height: 6, borderRadius: '50%', bgcolor: '#fff' }} />
        <Box sx={{ position: 'absolute', left: '44%', top: '56%', width: '12%', height: '9%', borderRadius: '50%', bgcolor: '#111827' }} />
        <Box sx={{ position: 'absolute', left: '39%', top: '66%', width: '22%', height: 8, borderBottom: '2px solid #111827', borderRadius: '0 0 99px 99px' }} />
      </Box>
      <Box sx={{ position: 'absolute', right: { xs: -5, md: -7 }, top: { xs: 18, md: 25 }, width: 18, height: 9,
        borderRadius: 99, bgcolor: '#fff', transform: 'rotate(38deg)', boxShadow: '0 0 0 3px #111827' }} />
    </Box>
  );
}

function StudentName({ student, size = 'medium', winner = false, subdued = false, placeholder }) {
  if (!student) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', minHeight: size === 'large' ? 96 : size === 'compact' ? 46 : 64, color: '#8b88a5',
        fontSize: size === 'compact' ? { xs: 13, md: 15 } : { xs: 16, md: 18 }, fontWeight: 800, lineHeight: 1.2 }}>
        {placeholder || 'Bir üst turdan gelecek'}
      </Box>
    );
  }
  const large = size === 'large';
  const compact = size === 'compact';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.1, md: large ? 2 : 1.2 }, minWidth: 0, opacity: subdued ? 0.62 : 1 }}>
      <StudentAvatar student={student} size={large ? 94 : compact ? 38 : 64} />
      <Typography sx={{
        minWidth: 0, color: winner ? '#6b4300' : ink, fontWeight: winner ? 1000 : 900,
        fontSize: large ? { xs: 28, md: 38 } : compact ? { xs: 15, md: 18 } : { xs: 22, md: 28 },
        lineHeight: 1.1, overflowWrap: 'anywhere', textShadow: '0 2px 0 rgba(255,255,255,0.8)',
      }}>
        {winner && <Box component="span" sx={{ mr: 0.6 }}>🏆</Box>}
        {student.display_name}
      </Typography>
    </Box>
  );
}

function questionFontSize(prompt) {
  const length = String(prompt || '').trim().length;
  if (length > 80) return { xs: 26, md: 'clamp(30px, 2.1vw, 36px)' };
  if (length > 52) return { xs: 28, md: 'clamp(34px, 2.4vw, 42px)' };
  return { xs: 31, md: 'clamp(38px, 2.9vw, 50px)' };
}

function MiniTaskHeader({ classCode }) {
  const brandLetters = [
    ['D', '#ef5b8a'], ['A', '#f59e0b'], ['N', '#84cc16'], ['D', '#22c55e'], ['U', '#38bdf8'],
  ];
  return (
    <Box sx={{
      position: 'relative', mx: { xs: -1.2, sm: -2, md: -3.2 }, mb: { xs: 1.5, md: 2 },
      minHeight: { xs: 82, md: 108 }, px: { xs: 1.4, sm: 2.4, md: 4 },
      borderRadius: '0 0 28px 28px', color: '#fff',
      background: 'linear-gradient(180deg, #6d45df 0%, #5128bc 100%)',
      boxShadow: '0 6px 0 rgba(39,22,108,0.24), 0 14px 28px rgba(88,28,135,0.18)',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      flexWrap: 'wrap', gap: 1.5, overflow: 'hidden',
    }}>
      <StarRoundedIcon sx={{ position: 'absolute', left: '60%', top: 26, fontSize: 42, color: '#facc15', transform: 'rotate(-12deg)', display: { xs: 'none', md: 'block' } }} />
      <Box sx={{ position: 'absolute', left: '70%', top: 19, width: 61, height: 23, bgcolor: '#fff', borderRadius: 99, opacity: 0.95, display: { xs: 'none', md: 'block' },
        '&::before, &::after': { content: '""', position: 'absolute', bgcolor: '#fff', borderRadius: '50%' },
        '&::before': { width: 28, height: 28, left: 9, top: -13 }, '&::after': { width: 33, height: 33, right: 8, top: -17 } }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 }, minWidth: 0, zIndex: 1 }}>
        <PandaMark />
        <Typography component="div" sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: { xs: 1, md: 2 }, fontSize: { xs: 29, sm: 39, md: 52 },
          fontWeight: 1000, lineHeight: 1, textShadow: '0 3px 0 rgba(15,23,42,0.16)' }}>
          <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
            {brandLetters.map(([letter, color], index) => (
              <Box component="span" key={index} sx={{ color, WebkitTextStroke: { xs: '1px #fff', md: '2px #fff' } }}>{letter}</Box>
            ))}
          </Box>
          <Box component="span" sx={{ color: '#fff' }}>Mini Görevler</Box>
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, zIndex: 1 }}>
        {classCode && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, bgcolor: 'rgba(39,22,108,0.48)', borderRadius: 99,
          px: { xs: 1.2, md: 2.2 }, py: 1, boxShadow: 'inset 0 -3px 0 rgba(15,23,42,0.16)' }}>
          <GroupsRoundedIcon sx={{ fontSize: { xs: 22, md: 29 } }} />
          <Typography sx={{ whiteSpace: 'nowrap', fontSize: { xs: 17, md: 26 }, fontWeight: 1000, lineHeight: 1 }}>{classCode} Sınıfı</Typography>
        </Box>}
      </Box>
    </Box>
  );
}

export function MatchSide({ student, question, onAnswer, busy, progress, questionCount, feedback, showProgress, imageUrls, offlineImages }) {
  const imageSrc = question?.image_url && (offlineImages ? imageUrls[question.image_url] : question.image_url);
  const hasDialogue = isMiniTaskDialogue(question?.dialogue);
  const hasQuestionImage = Boolean(imageSrc) || hasDialogue;
  const hasChoiceImages = question?.choices?.some((choice) => Boolean(question.choice_media?.[choice]?.url));
  const visualToWord = Boolean(imageSrc) && !hasDialogue && !hasChoiceImages;
  return (
    <Box sx={{ flex: 1, minWidth: 0, p: { xs: 1.5, md: 2 }, borderRadius: 5, bgcolor: getStudentAvatar(student?.id).bgColor,
      border: '3px solid rgba(255,255,255,0.95)', boxShadow: cardShadow }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', gap: { xs: 1, md: 2 } }}>
        <StudentName student={student} size="large" />
        {showProgress && <Box aria-label={`${student?.display_name} ilerleme`} sx={{
          px: { xs: 1.4, md: 2 }, py: 0.6, borderRadius: 99,
          bgcolor: feedback?.correct ? '#dcfce7' : '#fff', border: `2px solid ${feedback?.correct ? '#4ade80' : '#c4b5fd'}`,
          color: feedback?.correct ? '#166534' : violet, fontSize: { xs: 22, md: 28 }, fontWeight: 1000,
          boxShadow: '0 3px 0 rgba(91,33,182,0.1)', whiteSpace: 'nowrap',
        }}>
          {feedback?.correct ? '✓ ' : ''}{progress} / {questionCount}
        </Box>}
      </Box>
      {question ? (
        <>
          <Typography sx={{ my: hasQuestionImage ? { xs: 1, md: 1.1 } : { xs: 1.5, md: 1.7 }, textAlign: 'center', color: ink,
            fontSize: visualToWord ? { xs: 30, md: 'clamp(34px, 2.5vw, 44px)' } : questionFontSize(question.prompt), fontWeight: 1000, lineHeight: 1.13 }}>
            {question.prompt}
          </Typography>
          {hasDialogue ? <MiniTaskDialogue dialogue={question.dialogue} compact /> : imageSrc && <Box component="img" src={imageSrc} alt="" sx={{ display: 'block', mx: 'auto', mb: { xs: 1, md: 1.2 },
            width: visualToWord ? { xs: 122, md: 'min(19vh, 188px)' } : 'auto', height: visualToWord ? { xs: 122, md: 'min(19vh, 188px)' } : 'min(132px, 15vh)',
            maxWidth: '100%', objectFit: 'contain' }} />}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: hasChoiceImages ? { xs: 1, md: 1.5 } : { xs: 1, md: 1.5 },
            gridAutoRows: hasChoiceImages ? { xs: 104, md: 'min(14.2vh, 132px)' } : 'auto' }}>
            {question.choices.map((choice, index) => {
              const selected = feedback && String(feedback.choice) === String(choice);
              const choiceImage = question.choice_media?.[choice]?.url;
              const choiceBackground = selected
                ? feedback.correct ? '#dcfce7' : '#ffe4e6'
                : '#fff';
              const choiceOutline = selected ? feedback.correct ? '#22c55e' : '#fb7185' : 'transparent';
              return (
              <Button key={choice} aria-label={choiceImage ? choice : undefined} variant="contained" disabled={busy} onClick={() => onAnswer(choice)}
                sx={{ '&:focus-visible, &.Mui-focusVisible': { outline: '3px solid #4c1d95', outlineOffset: '3px' },
                  minWidth: choiceImage ? 0 : undefined, minHeight: choiceImage ? '100%' : visualToWord ? { xs: 56, md: 58 } : hasQuestionImage ? { xs: 54, md: 68 } : { xs: 62, md: 82 },
                  p: choiceImage ? 0 : undefined, justifySelf: choiceImage ? 'stretch' : 'stretch', borderRadius: 3.5,
                  background: choiceImage ? 'rgba(255,255,255,0.92)' : choiceBackground,
                  color: selected ? feedback.correct ? '#166534' : '#9f1239' : violet,
                  border: `3px solid ${choiceOutline === 'transparent' ? '#c4b5fd' : choiceOutline}`,
                  boxShadow: '0 5px 0 rgba(91,33,182,0.16)', fontSize: { xs: 23, md: 'clamp(25px, 1.9vw, 32px)' }, fontWeight: 1000,
                  justifyContent: choiceImage ? 'center' : undefined,
                  overflow: choiceImage ? 'hidden' : undefined,
                  '&:hover': { background: choiceImage ? '#fff' : selected ? choiceBackground : '#f5f3ff', borderColor: selected ? choiceOutline : '#8b5cf6',
                    transform: choiceImage ? 'translateY(-2px)' : undefined },
                  '&.Mui-disabled': { background: choiceImage ? 'rgba(255,255,255,0.92)' : choiceBackground,
                    color: selected ? feedback.correct ? '#166534' : '#9f1239' : violet,
                    borderColor: choiceOutline === 'transparent' ? '#c4b5fd' : choiceOutline } }}>
                {choiceImage ? <Box component="img" src={offlineImages ? imageUrls[choiceImage] : choiceImage} alt=""
                  sx={{ display: 'block', width: { xs: 88, md: 120 }, height: { xs: 88, md: 120 }, maxWidth: '100%', objectFit: 'contain' }} /> : choice}
              </Button>
              );
            })}
          </Box>
        </>
      ) : (
        <Typography sx={{ mt: 5, textAlign: 'center', color: violet, fontSize: { xs: 21, md: 26 }, fontWeight: 900 }}>
          {showProgress ? 'Sorularını tamamladı!' : 'Cevap bekleniyor'}
        </Typography>
      )}
    </Box>
  );
}

function MatchCard({ match, nextMatchId, lastRound }) {
  const isFinal = match.round === lastRound;
  const isNext = match.id === nextMatchId;
  const isBye = match.status === 'completed' && Boolean(match.winner) && (!match.student_a || !match.student_b);
  const winnerId = String(match.winner?.id || '');
  const sides = [['a', match.student_a], ['b', match.student_b]];
  return (
    <Box sx={{
      minWidth: 0, p: { xs: 1.5, md: isFinal ? 2.5 : 1.7 }, borderRadius: 4,
      bgcolor: isFinal ? '#fff9e7' : isNext ? '#f2ebff' : '#fff',
      border: `3px solid ${isNext ? '#8b5cf6' : isFinal ? '#facc15' : '#e4d9fc'}`,
      boxShadow: isNext ? '0 6px 0 rgba(91,33,182,0.2), 0 14px 30px rgba(124,58,237,0.16)' : cardShadow,
      display: 'flex', flexDirection: 'column', gap: 0.7,
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: 26, mb: 0.2 }}>
        <Typography sx={{ fontSize: { xs: 14, md: 16 }, fontWeight: 1000, color: isFinal ? '#a16207' : violet }}>
          {isFinal ? 'Şampiyonluk karşılaşması' : `${match.slot + 1}. karşılaşma`}
        </Typography>
        {isNext && <Box sx={{ px: 1.2, py: 0.35, borderRadius: 99, bgcolor: '#7c3aed', color: '#fff', fontSize: 13, fontWeight: 1000 }}>Sıradaki</Box>}
      </Box>
      {match.activity?.label && <Typography sx={{ alignSelf: 'flex-start', px: 1.2, py: 0.3, borderRadius: 99,
        bgcolor: '#ede9fe', color: violet, fontSize: 14, fontWeight: 900 }}>{activityLabel(match.activity)}</Typography>}
      {isBye ? (
        <>
          <StudentName student={match.winner} winner />
          <Typography sx={{ pl: 0.6, fontSize: { xs: 15, md: 17 }, color: '#6d28d9', fontWeight: 900 }}>⭐ Doğrudan üst turda</Typography>
        </>
      ) : <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)', alignItems: 'center', gap: { xs: 0.6, md: 1 } }}>
        {sides.map(([side, student], index) => (
          <React.Fragment key={side}>
            {index === 1 && <Typography aria-hidden="true" sx={{ textAlign: 'center', color: '#8b5cf6', fontWeight: 1000, fontSize: { xs: 12, md: 14 } }}>VS</Typography>}
            <Box sx={{ display: 'flex', justifyContent: index === 0 ? 'flex-start' : 'flex-end', px: 0.7, py: 0.7, minWidth: 0, borderRadius: 3,
              bgcolor: winnerId && String(student?.id) === winnerId ? '#fff0b5' : 'transparent' }}>
              <StudentName student={student} size="compact" placeholder={sourceMatchLabel(match, side, lastRound)}
                winner={Boolean(winnerId && String(student?.id) === winnerId)}
                subdued={Boolean(winnerId && student && String(student.id) !== winnerId)} />
            </Box>
          </React.Fragment>
        ))}
      </Box>}
      {match.winner && !isFinal && !isBye && (
        <Typography sx={{ pl: 0.6, mt: 0.2, color: '#6b4300', fontWeight: 900, fontSize: 15 }}>Üst tura geçti ↓</Typography>
      )}
    </Box>
  );
}

function Bracket({ matches, nextMatchId }) {
  const rounds = [...new Set(matches.map((match) => match.round))].sort((a, b) => a - b);
  const lastRound = rounds[rounds.length - 1];
  return (
    <Box component="section" aria-label="Turnuva yolu" sx={{ pb: 6 }}>
      <Typography sx={{ textAlign: 'center', fontSize: { xs: 28, md: 38 }, fontWeight: 1000, color: violet, mb: 2.5 }}>
        Turnuva Yolu
      </Typography>
      {rounds.map((round, roundIndex) => {
        const roundMatches = matches.filter((match) => match.round === round).sort((a, b) => a.slot - b.slot);
        const isFinal = round === lastRound;
        return (
          <React.Fragment key={round}>
            {roundIndex > 0 && <Box aria-hidden="true" sx={{ display: 'flex', justifyContent: 'center', color: '#a78bfa', my: { xs: 1.5, md: 2 } }}>
              <SouthRoundedIcon sx={{ fontSize: { xs: 36, md: 46 } }} />
            </Box>}
            <Box component="section" aria-label={roundTitle(round, lastRound)} sx={{
              p: { xs: 1.5, md: 2.4 }, borderRadius: 5,
              bgcolor: isFinal ? '#fff1c5' : roundIndex % 2 === 0 ? '#eee9ff' : '#e5f4ff',
              border: `2px solid ${isFinal ? '#f4c64b' : roundIndex % 2 === 0 ? '#d8cafe' : '#beddf8'}`,
              boxShadow: '0 6px 0 rgba(39,22,108,0.07)',
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 1.5, md: 2 } }}>
                <Typography component="h2" sx={{
                  bgcolor: isFinal ? '#facc15' : '#6d45df', color: isFinal ? '#713f12' : '#fff',
                  px: { xs: 2.3, md: 3.5 }, py: { xs: 0.7, md: 0.9 }, borderRadius: 99,
                  fontSize: { xs: 22, md: isFinal ? 34 : 27 }, fontWeight: 1000,
                  boxShadow: isFinal ? '0 4px 0 #d99d19' : '0 4px 0 #4c1d95',
                }}>
                  {isFinal ? '🏆 ' : ''}{roundTitle(round, lastRound)}
                </Typography>
              </Box>
              <Box sx={{
                display: 'grid', justifyContent: 'center', gap: { xs: 1.5, md: 2 },
                width: '100%', maxWidth: roundMatches.length === 1 ? 700 : roundMatches.length === 2 ? 980 : '100%', mx: 'auto',
                gridTemplateColumns: roundMatches.length === 1
                  ? 'minmax(0, 700px)'
                  : { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))',
                    lg: `repeat(${Math.min(3, roundMatches.length)}, minmax(0, 1fr))`,
                    xl: `repeat(${Math.min(4, roundMatches.length)}, minmax(0, 1fr))` },
              }}>
                {roundMatches.map((match) => <MatchCard key={match.id} match={match} nextMatchId={nextMatchId} lastRound={lastRound} />)}
              </Box>
            </Box>
          </React.Fragment>
        );
      })}
    </Box>
  );
}

function NextMatch({ match, onStart, busy, lastRound }) {
  return (
    <Box sx={{
      mb: { xs: 3, md: 4 }, p: { xs: 2, md: 3 }, borderRadius: 5,
      bgcolor: '#eee9ff', border: '3px solid #a78bfa',
      boxShadow: '0 7px 0 rgba(91,33,182,0.16), 0 18px 34px rgba(91,33,182,0.12)',
      textAlign: 'center',
    }}>
      <Typography sx={{ color: violet, letterSpacing: 1, fontSize: { xs: 20, md: 27 }, fontWeight: 1000 }}>
        SIRADAKİ KARŞILAŞMA
      </Typography>
      <Typography sx={{ color: '#6d28d9', fontSize: { xs: 16, md: 19 }, fontWeight: 900, mt: 0.2 }}>
        {roundTitle(match.round, lastRound)}
      </Typography>
      {match.activity?.label && <Typography sx={{ mt: 0.8, color: violet, fontSize: { xs: 18, md: 23 }, fontWeight: 900 }}>
        {activityLabel(match.activity)}
      </Typography>}
      <Box sx={{ my: { xs: 2, md: 2.5 }, display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: { xs: 1, md: 3 }, flexWrap: 'wrap' }}>
        <Box sx={{ p: { xs: 1, md: 1.5 }, borderRadius: 4, bgcolor: '#fff', minWidth: { xs: '38%', md: 260 }, boxShadow: cardShadow }}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}><StudentName student={match.student_a} size="large" /></Box>
        </Box>
        <Typography sx={{ color: '#f59e0b', fontSize: { xs: 25, md: 39 }, fontWeight: 1000, textShadow: '0 2px 0 #fff' }}>VS</Typography>
        <Box sx={{ p: { xs: 1, md: 1.5 }, borderRadius: 4, bgcolor: '#fff', minWidth: { xs: '38%', md: 260 }, boxShadow: cardShadow }}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}><StudentName student={match.student_b} size="large" /></Box>
        </Box>
      </Box>
      <Button variant="contained" startIcon={<PlayArrowRoundedIcon />} disabled={busy} onClick={onStart} sx={actionButton}>
        Karşılaşmayı Başlat
      </Button>
    </Box>
  );
}

function ChampionCard({ champion, soundKey }) {
  useCelebrationSound(soundKey, 'champion');
  const confetti = [
    { left: '6%', top: '20%', color: '#ef5b8a', delay: '0ms', rotate: '22deg' },
    { left: '15%', top: '8%', color: '#38bdf8', delay: '150ms', rotate: '-30deg' },
    { left: '27%', top: '34%', color: '#84cc16', delay: '80ms', rotate: '16deg' },
    { right: '7%', top: '22%', color: '#a855f7', delay: '110ms', rotate: '-18deg' },
    { right: '16%', top: '7%', color: '#f97316', delay: '45ms', rotate: '28deg' },
    { right: '28%', top: '37%', color: '#facc15', delay: '190ms', rotate: '-24deg' },
  ];
  return (
    <Box sx={{ position: 'relative', maxWidth: 820, mx: 'auto', mb: 4, p: { xs: 3, md: 5 }, textAlign: 'center', overflow: 'hidden',
      borderRadius: 6, background: 'radial-gradient(circle at 50% 35%, #fffdf2 0%, #fff3bf 42%, #ffe7a0 100%)',
      border: '4px solid #facc15', boxShadow: '0 9px 0 rgba(217,158,25,0.24), 0 25px 44px rgba(161,98,7,0.18)',
      '@keyframes championConfetti': {
        '0%': { opacity: 0, transform: 'translateY(20px) scale(0.35) rotate(0deg)' },
        '22%': { opacity: 1 },
        '100%': { opacity: 0, transform: 'translateY(-125px) scale(1.1) rotate(190deg)' },
      },
      '@keyframes championFloat': {
        '0%, 100%': { transform: 'translateY(0) rotate(-3deg)' },
        '50%': { transform: 'translateY(-10px) rotate(3deg)' },
      },
      '@keyframes championRing': {
        '0%, 100%': { transform: 'scale(0.96)', opacity: 0.5 },
        '50%': { transform: 'scale(1.08)', opacity: 1 },
      },
      '@media (prefers-reduced-motion: reduce)': {
        '& .champion-confetti, & .champion-trophy, & .champion-ring': { animation: 'none !important' },
        '& .champion-confetti': { opacity: 0 },
      },
    }}>
      {confetti.map((piece, index) => <Box key={index} aria-hidden="true" className="champion-confetti" sx={{
        position: 'absolute', width: { xs: 9, md: 12 }, height: { xs: 14, md: 19 }, borderRadius: 1, bgcolor: piece.color,
        left: piece.left, right: piece.right, top: piece.top, transform: `rotate(${piece.rotate})`,
        animation: `championConfetti 2.2s ease-out ${piece.delay} infinite`,
      }} />)}
      <Typography sx={{ position: 'relative', color: '#a16207', fontSize: { xs: 24, md: 33 }, fontWeight: 1000, letterSpacing: 1.1 }}>
        🏆 SINIF ŞAMPİYONU 🏆
      </Typography>
      <Typography sx={{ position: 'relative', mt: 0.4, color: '#8a5b09', fontSize: { xs: 17, md: 21 }, fontWeight: 900 }}>
        Büyük finalin kazananı
      </Typography>
      <Box className="champion-trophy" sx={{ position: 'relative', display: 'inline-flex', my: { xs: 0.4, md: 0.8 }, animation: 'championFloat 1.7s ease-in-out infinite' }}>
        <EmojiEventsRoundedIcon sx={{ color: '#f59e0b', fontSize: { xs: 75, md: 108 }, filter: 'drop-shadow(0 5px 0 rgba(161,98,7,0.18))' }} />
      </Box>
      <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box sx={{ position: 'relative', display: 'inline-grid', placeItems: 'center' }}>
          <Box className="champion-ring" aria-hidden="true" sx={{ position: 'absolute', width: { xs: 142, md: 180 }, height: { xs: 142, md: 180 },
            borderRadius: '50%', border: '4px dashed rgba(217,158,25,0.72)', animation: 'championRing 1.7s ease-in-out infinite' }} />
          <StudentAvatar student={champion} size={140} />
          <Box aria-hidden="true" sx={{ position: 'absolute', top: -15, right: -12, display: 'grid', placeItems: 'center', width: 46, height: 46,
            borderRadius: '50%', bgcolor: '#7c3aed', border: '3px solid #fff', boxShadow: '0 4px 0 #4c1d95', fontSize: 27 }}>👑</Box>
        </Box>
        <Typography sx={{ mt: 1.3, color: ink, fontSize: { xs: 46, md: 66 }, fontWeight: 1000, lineHeight: 1.05, textShadow: '0 3px 0 rgba(255,255,255,0.9)' }}>
          {champion?.display_name}
        </Typography>
        <Typography sx={{ mt: 0.7, color: '#8a5b09', fontSize: { xs: 19, md: 25 }, fontWeight: 1000 }}>
          Tebrikler {champion?.display_name}! 🎉
        </Typography>
      </Box>
    </Box>
  );
}

function AdvanceCard({ winner, soundKey, soundPan = 0, children }) {
  useCelebrationSound(soundKey, 'advance', soundPan);
  const confetti = [
    { left: '9%', top: '17%', color: '#f59e0b', delay: '0ms', rotate: '18deg' },
    { left: '21%', top: '8%', color: '#ef5b8a', delay: '90ms', rotate: '-24deg' },
    { right: '18%', top: '13%', color: '#38bdf8', delay: '45ms', rotate: '26deg' },
    { right: '8%', top: '28%', color: '#a855f7', delay: '135ms', rotate: '-16deg' },
    { left: '29%', top: '52%', color: '#84cc16', delay: '170ms', rotate: '32deg' },
    { right: '28%', top: '48%', color: '#f97316', delay: '115ms', rotate: '-30deg' },
  ];
  return (
    <Box sx={{ position: 'relative', maxWidth: 780, mx: 'auto', mb: 3, p: { xs: 3, md: 4 }, textAlign: 'center', overflow: 'hidden',
      borderRadius: 5, bgcolor: '#eefbe4', border: '3px solid #a3d977', boxShadow: cardShadow,
      '@keyframes advanceConfetti': {
        '0%': { opacity: 0, transform: 'translateY(12px) scale(0.4) rotate(0deg)' },
        '25%': { opacity: 1 },
        '100%': { opacity: 0, transform: 'translateY(-74px) scale(1) rotate(150deg)' },
      },
      '@media (prefers-reduced-motion: reduce)': { '& .advance-confetti': { animation: 'none !important', opacity: 0 } },
    }}>
      {confetti.map((piece, index) => <Box key={index} aria-hidden="true" className="advance-confetti" sx={{
        position: 'absolute', width: { xs: 8, md: 11 }, height: { xs: 13, md: 17 }, borderRadius: 1,
        bgcolor: piece.color, left: piece.left, right: piece.right, top: piece.top,
        transform: `rotate(${piece.rotate})`, animation: `advanceConfetti 1.8s ease-out ${piece.delay} infinite`,
      }} />)}
      <Typography sx={{ position: 'relative', color: '#3f7b25', fontSize: { xs: 27, md: 38 }, fontWeight: 1000 }}>
        🎉 Bir Üst Tura!
      </Typography>
      <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1.5 }}>
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <StudentAvatar student={winner} size={112} />
          <Box aria-hidden="true" sx={{ position: 'absolute', right: -12, top: -10, display: 'grid', placeItems: 'center', width: 39, height: 39,
            borderRadius: '50%', bgcolor: '#fef3c7', border: '3px solid #fff', boxShadow: '0 3px 0 rgba(161,98,7,0.2)', fontSize: 23 }}>🏆</Box>
        </Box>
        <Typography sx={{ mt: 0.8, color: ink, fontSize: { xs: 30, md: 40 }, fontWeight: 1000, lineHeight: 1.1 }}>
          {winner?.display_name}
        </Typography>
        <Typography sx={{ mt: 0.45, color: '#3f7b25', fontSize: { xs: 18, md: 23 }, fontWeight: 900 }}>
          Karşılaşmayı kazandı!
        </Typography>
      </Box>
      {children}
    </Box>
  );
}

export default function TournamentPage() {
  const { token } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const startNewTournamentRef = useRef(location.state?.startNewTournament === true);
  const [board, setBoard] = useState(null);
  const [tournament, setTournament] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [contentTopics, setContentTopics] = useState([]);
  const [contentMode, setContentMode] = useState('mixed');
  const [selectedLessonCode, setSelectedLessonCode] = useState('');
  const [selectedContentKey, setSelectedContentKey] = useState('');
  const [focusMatchId, setFocusMatchId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [answerBusy, setAnswerBusy] = useState({ a: false, b: false });
  const [answerFeedback, setAnswerFeedback] = useState({ a: null, b: null });
  const [localMatch, setLocalMatch] = useState(null);
  const [preparing, setPreparing] = useState(false);
  const [submissionFailed, setSubmissionFailed] = useState(false);
  const [imageUrls, setImageUrls] = useState({});
  const [offlineActive, setOfflineActive] = useState(false);
  const [offlineResults, setOfflineResults] = useState([]);
  const [endDialogOpen, setEndDialogOpen] = useState(false);
  const [error, setError] = useState('');
  const [remainingMs, setRemainingMs] = useState(60000);
  const deadlineRef = useRef(null);
  const refreshId = useRef(0);
  const finishingRef = useRef(false);
  const localMatchRef = useRef(null);
  const submissionAttemptedRef = useRef(false);
  const offlineCompletedMatchRef = useRef(null);
  const imageUrlsRef = useRef({});
  const boardSessionId = board?.board_session?.id;
  const lessonOptions = useMemo(() => [...new Map(contentTopics.map((topic) => [topic.lesson_code, topic.lesson_name])).entries()], [contentTopics]);
  const selectedTopic = contentTopics.find((topic) => topic.key === selectedContentKey);
  const contentLabel = contentMode === 'mixed' ? 'Karışık'
    : selectedTopic ? `${selectedTopic.lesson_name} / ${selectedTopic.label}` : 'Konu seçilmedi';

  const loadContentTopics = useCallback(async (sessionId, students) => {
    const payload = await getTournamentContents(token, sessionId);
    const topics = payload.topics || [];
    const absentIds = new Set((payload.absent_student_ids || []).map(Number));
    setContentTopics(topics);
    setSelectedLessonCode(topics[0]?.lesson_code || '');
    setSelectedContentKey(topics[0]?.key || '');
    setSelectedIds(students.filter((student) => !absentIds.has(Number(student.id)))
      .map((student) => Number(student.id)));
  }, [token]);

  const installImageUrls = useCallback((next) => {
    Object.values(imageUrlsRef.current).forEach((url) => {
      if (url.startsWith('blob:')) window.URL.revokeObjectURL(url);
    });
    imageUrlsRef.current = next;
    setImageUrls(next);
  }, []);

  const refresh = useCallback(async (sessionId) => {
    const requestId = ++refreshId.current;
    const payload = await getCurrentTournament(token, sessionId);
    if (requestId === refreshId.current) setTournament(payload.tournament);
    return payload.tournament;
  }, [token]);

  const prepareLocalMatch = useCallback(async (match, tournamentId) => {
    setPreparing(true);
    try {
      installImageUrls(await preloadTournamentImages(match.question_bundle));
      const key = localMatchStorageKey(token, tournamentId, match.id);
      let saved = null;
      try {
        saved = JSON.parse(window.localStorage.getItem(key) || 'null');
      } catch {
        saved = null;
      }
      const validSaved = saved?.matchId === match.id && Array.isArray(saved.attempts)
        && saved.progress && Number.isFinite(saved.startedAt);
      const state = tickLocalMatch(validSaved ? saved : createLocalMatch(match), match.question_bundle);
      window.localStorage.setItem(key, JSON.stringify(state));
      localMatchRef.current = state;
      submissionAttemptedRef.current = false;
      setLocalMatch(state);
      setRemainingMs(Math.max(0, matchDurationMs(match.question_bundle) - elapsedLocalMs(state)));
      setError('');
    } finally {
      setPreparing(false);
    }
  }, [installImageUrls, token]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        let saved = null;
        try { saved = JSON.parse(window.localStorage.getItem(localTournamentStorageKey(token)) || 'null'); } catch { saved = null; }
        if (saved?.board && saved?.tournament?.mode === 'local_tournament' && Array.isArray(saved.results)) {
          setBoard(saved.board);
          setTournament(saved.tournament);
          setOfflineResults(saved.results);
          setFocusMatchId(saved.focusMatchId ?? null);
          if (saved.localMatch) {
            const match = saved.tournament.matches.find((item) => item.id === saved.localMatch.matchId);
            if (match?.question_bundle) {
              const state = tickLocalMatch(saved.localMatch, match.question_bundle);
              localMatchRef.current = state;
              setLocalMatch(state);
              setRemainingMs(Math.max(0, matchDurationMs(match.question_bundle) - elapsedLocalMs(state)));
            }
          }
          installImageUrls(await preloadTournamentImages(saved.tournament));
          if (mounted) setOfflineActive(true);
          return;
        }
        const stored = await readStoredBoardSession(token);
        const nextBoard = await fetchBoard(token, stored?.board_session_id);
        await writeStoredBoardSession(token, nextBoard.board_session);
        const startNewTournament = startNewTournamentRef.current;
        const current = startNewTournament
          ? { tournament: null }
          : await getCurrentTournament(token, nextBoard.board_session.id);
        if (!current.tournament) {
          await loadContentTopics(nextBoard.board_session.id, nextBoard.students);
        }
        if (!mounted) return;
        setBoard(nextBoard);
        setTournament(current.tournament);
        const currentMatch = current.tournament?.matches.find((match) => ['active', 'golden'].includes(match.status));
        setFocusMatchId(currentMatch?.id || null);
        if (startNewTournament) {
          startNewTournamentRef.current = false;
          navigate(location.pathname, { replace: true });
        }
        if (currentMatch?.mode === 'local_batch') await prepareLocalMatch(currentMatch, current.tournament.id);
        if (current.tournament?.mode === 'local_tournament' && current.tournament.status === 'active') {
          installImageUrls(await preloadTournamentImages(current.tournament));
          if (mounted) setOfflineActive(true);
        }
      } catch (err) {
        if (mounted) setError(err.message || 'Turnuva açılamadı.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [installImageUrls, loadContentTopics, location.pathname, navigate, prepareLocalMatch, token]);

  useEffect(() => {
    if (!offlineActive || !board || !tournament || tournament.mode !== 'local_tournament') return;
    window.localStorage.setItem(localTournamentStorageKey(token), JSON.stringify({
      board, tournament, results: offlineResults, focusMatchId, localMatch,
    }));
  }, [board, focusMatchId, localMatch, offlineActive, offlineResults, token, tournament]);

  const matches = useMemo(() => tournament?.matches || [], [tournament?.matches]);
  const focusedMatch = matches.find((match) => match.id === focusMatchId);
  const localFocused = ['local_batch', 'local_tournament'].includes(focusedMatch?.mode)
    && localMatch?.matchId === focusedMatch.id;
  const displayMatch = localFocused && localMatch.phase !== 'completed' ? {
    ...focusedMatch,
    status: localMatch.phase === 'golden' ? 'golden' : 'active',
    progress_a: localMatch.progress.a,
    progress_b: localMatch.progress.b,
    question_count: focusedMatch.question_bundle.questions.length,
    question_a: questionForSide(localMatch, focusedMatch.question_bundle, 'a'),
    question_b: questionForSide(localMatch, focusedMatch.question_bundle, 'b'),
  } : focusedMatch;
  const activeMatch = useMemo(() => matches.find((match) => ['active', 'golden'].includes(match.status)), [matches]);
  const nextMatch = useMemo(() => matches.find((match) => match.status === 'ready'), [matches]);
  const lastRound = Math.max(1, ...matches.map((match) => match.round));
  const goldenWinnerSide = ['a', 'b'].find((side) => answerFeedback[side]?.phase === 'golden' && answerFeedback[side]?.correct);
  const goldenWinner = goldenWinnerSide && focusedMatch?.[`student_${goldenWinnerSide}`];

  const resetExpiredTournamentSession = useCallback(async () => {
    window.localStorage.removeItem(localTournamentStorageKey(token));
    if (tournament?.id && focusedMatch?.id) {
      window.localStorage.removeItem(localMatchStorageKey(token, tournament.id, focusedMatch.id));
    }
    installImageUrls({});
    localMatchRef.current = null;
    submissionAttemptedRef.current = false;
    offlineCompletedMatchRef.current = null;
    setLocalMatch(null);
    setOfflineActive(false);
    setOfflineResults([]);
    setSubmissionFailed(false);
    setFocusMatchId(null);
    setTournament(null);

    const nextBoard = await fetchBoard(token);
    await writeStoredBoardSession(token, nextBoard.board_session);
    setBoard(nextBoard);
    await loadContentTopics(nextBoard.board_session.id, nextBoard.students);
    window.localStorage.removeItem(localTournamentStorageKey(token));
  }, [focusedMatch?.id, installImageUrls, loadContentTopics, token, tournament?.id]);

  useEffect(() => {
    if (!activeMatch || ['local_batch', 'local_tournament'].includes(activeMatch.mode) || activeMatch.status !== 'active' || !tournament?.server_now) {
      deadlineRef.current = null;
      return;
    }
    const ms = Math.max(0, Date.parse(activeMatch.deadline_at) - Date.parse(tournament.server_now));
    deadlineRef.current = Date.now() + ms;
    setRemainingMs(ms);
  }, [activeMatch, tournament?.server_now]);

  useEffect(() => {
    if (!activeMatch || ['local_batch', 'local_tournament'].includes(activeMatch.mode) || activeMatch.status !== 'active' || !boardSessionId) return undefined;
    const interval = window.setInterval(async () => {
      const ms = Math.max(0, (deadlineRef.current || Date.now()) - Date.now());
      setRemainingMs(ms);
      if (ms > 0 || finishingRef.current) return;
      finishingRef.current = true;
      try {
        await finishTournamentMatch(token, boardSessionId, tournament.id, activeMatch.id);
        await refresh(boardSessionId);
      } catch (err) {
        setError(err.message || 'Maç sonuçlandırılamadı.');
      } finally {
        finishingRef.current = false;
      }
    }, 250);
    return () => window.clearInterval(interval);
  }, [activeMatch, boardSessionId, refresh, token, tournament?.id]);

  useEffect(() => {
    if (!localFocused || !localMatch || localMatch.phase !== 'normal' || (focusedMatch.mode === 'local_tournament' && !offlineActive)) return undefined;
    const interval = window.setInterval(() => {
      const current = localMatchRef.current;
      if (!current || current.phase !== 'normal') return;
      const next = tickLocalMatch(current, focusedMatch.question_bundle);
      setRemainingMs(Math.max(0, matchDurationMs(focusedMatch.question_bundle) - elapsedLocalMs(next)));
      if (next !== current) {
        localMatchRef.current = next;
        if (focusedMatch.mode === 'local_batch') {
          window.localStorage.setItem(localMatchStorageKey(token, tournament.id, focusedMatch.id), JSON.stringify(next));
        }
        setLocalMatch(next);
      }
    }, 200);
    return () => window.clearInterval(interval);
  }, [focusedMatch, localFocused, localMatch, offlineActive, token, tournament?.id]);

  const submitLocalResult = useCallback(async () => {
    const current = localMatchRef.current;
    if (!current || current.phase !== 'completed' || finishingRef.current || !boardSessionId || !tournament) return;
    finishingRef.current = true;
    setSubmissionFailed(false);
    setError('');
    try {
      const result = await finishTournamentMatch(token, boardSessionId, tournament.id, current.matchId, {
        attempts: current.attempts,
        elapsed_ms: elapsedLocalMs(current),
      });
      window.localStorage.removeItem(localMatchStorageKey(token, tournament.id, current.matchId));
      installImageUrls({});
      localMatchRef.current = null;
      setLocalMatch(null);
      setTournament(result);
    } catch (err) {
      if (err?.status === 410) {
        try {
          await resetExpiredTournamentSession();
          setError('Turnuva oturumunun süresi doldu. Sonuç kaydedilemedi; yeni bir turnuva hazırlayabilirsiniz.');
        } catch (recoveryError) {
          setError(recoveryError.message || 'Turnuva oturumu yenilenemedi. Sayfayı yeniden açın.');
        }
        return;
      }
      setSubmissionFailed(true);
      setError(err.message || 'Karşılaşma sonucu kaydedilemedi.');
    } finally {
      finishingRef.current = false;
    }
  }, [boardSessionId, installImageUrls, resetExpiredTournamentSession, token, tournament]);

  useEffect(() => {
    if (localMatch?.phase !== 'completed' || submissionAttemptedRef.current) return;
    if (focusedMatch?.mode === 'local_tournament') return;
    submissionAttemptedRef.current = true;
    submitLocalResult();
  }, [focusedMatch?.mode, localMatch?.phase, submitLocalResult]);

  useEffect(() => {
    if (!offlineActive || focusedMatch?.mode !== 'local_tournament' || localMatch?.phase !== 'completed') return;
    if (offlineCompletedMatchRef.current === focusedMatch.id) return;
    offlineCompletedMatchRef.current = focusedMatch.id;
    try {
      const next = advanceLocalTournament(tournament, focusedMatch.id, localMatch.winnerSide);
      setOfflineResults((current) => [...current, {
        match_id: focusedMatch.id, attempts: localMatch.attempts, elapsed_ms: elapsedLocalMs(localMatch),
      }]);
      setTournament(next);
      localMatchRef.current = null;
      setLocalMatch(null);
    } catch (err) {
      offlineCompletedMatchRef.current = null;
      setError(err.message || 'Eşleşme tamamlanamadı.');
    }
  }, [focusedMatch, localMatch, offlineActive, tournament]);

  const submitOfflineTournament = useCallback(async () => {
    if (!offlineActive || tournament?.status !== 'completed' || finishingRef.current || !boardSessionId) return;
    finishingRef.current = true;
    setSubmissionFailed(false);
    setError('');
    try {
      const result = await finishTournament(token, boardSessionId, tournament.id, offlineResults);
      window.localStorage.removeItem(localTournamentStorageKey(token));
      installImageUrls({});
      setOfflineActive(false);
      setTournament(result);
    } catch (err) {
      if (err?.status === 410) {
        try {
          await resetExpiredTournamentSession();
          setError('Turnuva oturumunun süresi doldu. Sonuç kaydedilemedi; yeni bir turnuva hazırlayabilirsiniz.');
        } catch (recoveryError) {
          setError(recoveryError.message || 'Turnuva oturumu yenilenemedi. Sayfayı yeniden açın.');
        }
        return;
      }
      setSubmissionFailed(true);
      setError(err.message || 'Turnuva sonucu kaydedilemedi.');
    } finally {
      finishingRef.current = false;
    }
  }, [boardSessionId, installImageUrls, offlineActive, offlineResults, resetExpiredTournamentSession, token, tournament]);

  useEffect(() => {
    if (!offlineActive || tournament?.status !== 'completed' || submissionAttemptedRef.current) return;
    submissionAttemptedRef.current = true;
    submitOfflineTournament();
  }, [offlineActive, submitOfflineTournament, tournament?.status]);

  const run = async (action) => {
    setBusy(true);
    setError('');
    try { await action(); } catch (err) { setError(err.message || 'İşlem tamamlanamadı.'); }
    finally { setBusy(false); }
  };

  const endTournament = () => run(async () => {
    if (!tournament || !boardSessionId) return;
    try {
      await abandonTournament(token, boardSessionId, tournament.id);
    } catch (err) {
      if (err?.status !== 410) throw err;
      setEndDialogOpen(false);
      await resetExpiredTournamentSession();
      return;
    }
    window.localStorage.removeItem(localTournamentStorageKey(token));
    if (focusedMatch) window.localStorage.removeItem(localMatchStorageKey(token, tournament.id, focusedMatch.id));
    installImageUrls({});
    localMatchRef.current = null;
    setLocalMatch(null);
    setOfflineActive(false);
    setOfflineResults([]);
    setFocusMatchId(null);
    setTournament(null);
    setEndDialogOpen(false);
    const nextBoard = await fetchBoard(token, boardSessionId);
    await writeStoredBoardSession(token, nextBoard.board_session);
    setBoard(nextBoard);
    await loadContentTopics(nextBoard.board_session.id, nextBoard.students);
  });

  const prepareNewTournament = () => {
    setContentMode('mixed');
    setFocusMatchId(null);
    setTournament(null);
    run(() => loadContentTopics(boardSessionId, board?.students || []));
  };

  const beginTournament = () => run(async () => {
    prepareMiniTaskSound(readMiniTaskSoundEnabled());
    const created = await createTournament(token, boardSessionId, selectedIds,
      contentMode === 'mixed' ? 'mixed' : selectedContentKey);
    if (created.board_session_expires_at && board?.board_session) {
      const nextBoard = {
        ...board,
        board_session: { ...board.board_session, expires_at: created.board_session_expires_at },
      };
      await writeStoredBoardSession(token, nextBoard.board_session);
      setBoard(nextBoard);
    }
    setTournament(created);
    setFocusMatchId(null);
    if (created.mode === 'local_tournament') {
      installImageUrls(await preloadTournamentImages(created));
      setOfflineResults([]);
      setOfflineActive(true);
      submissionAttemptedRef.current = false;
    }
  });

  const beginMatch = () => run(async () => {
    prepareMiniTaskSound(readMiniTaskSoundEnabled());
    if (tournament.mode === 'local_tournament') {
      const state = createLocalMatch(nextMatch);
      localMatchRef.current = state;
      offlineCompletedMatchRef.current = null;
      setLocalMatch(state);
      setRemainingMs(matchDurationMs(nextMatch.question_bundle));
      setAnswerFeedback({ a: null, b: null });
      setTournament(startLocalTournamentMatch(tournament, nextMatch.id));
      setFocusMatchId(nextMatch.id);
      return;
    }
    const started = await startTournamentMatch(token, boardSessionId, tournament.id, nextMatch.id);
    setTournament(started);
    setAnswerFeedback({ a: null, b: null });
    setFocusMatchId(nextMatch.id);
    const startedMatch = started.matches.find((match) => match.id === nextMatch.id);
    if (startedMatch?.mode === 'local_batch') await prepareLocalMatch(startedMatch, started.id);
  });

  const answer = async (side, choice) => {
    if (!focusedMatch || answerBusy[side]) return;
    if (['local_batch', 'local_tournament'].includes(focusedMatch.mode)) {
      const current = localMatchRef.current;
      if (!current || current.matchId !== focusedMatch.id || current.phase === 'completed') return;
      const { state, feedback } = answerLocalMatch(current, focusedMatch.question_bundle, side, choice);
      if (state !== current) {
        localMatchRef.current = state;
        if (focusedMatch.mode === 'local_batch') {
          window.localStorage.setItem(localMatchStorageKey(token, tournament.id, focusedMatch.id), JSON.stringify(state));
        }
        setLocalMatch(state);
      }
      if (feedback) {
        playMiniTaskSound(feedback.correct ? 'correct' : 'wrong', readMiniTaskSoundEnabled(), {
          pan: tournamentSoundPan[side],
        });
        setAnswerBusy((previous) => ({ ...previous, [side]: true }));
        setAnswerFeedback((previous) => ({ ...previous, [side]: feedback }));
        window.setTimeout(() => {
          setAnswerFeedback((previous) => ({ ...previous, [side]: null }));
          setAnswerBusy((previous) => ({ ...previous, [side]: false }));
        }, feedback.phase === 'golden' && feedback.correct ? 850 : 280);
      }
      return;
    }
    if (focusedMatch.status === 'active' && deadlineRef.current && Date.now() >= deadlineRef.current) return;
    const student = side === 'a' ? focusedMatch.student_a : focusedMatch.student_b;
    const question = side === 'a' ? focusedMatch.question_a : focusedMatch.question_b;
    if (!student || !question) return;
    setAnswerBusy((state) => ({ ...state, [side]: true }));
    setError('');
    try {
      const payload = await answerTournamentQuestion(token, boardSessionId, tournament.id, focusedMatch.id,
        student.id, { phase: question.phase, index: question.index, answer: choice });
      if (payload.answer_feedback) {
        playMiniTaskSound(payload.answer_feedback.correct ? 'correct' : 'wrong', readMiniTaskSoundEnabled(), {
          pan: tournamentSoundPan[side],
        });
        setAnswerFeedback((state) => ({ ...state, [side]: { ...payload.answer_feedback, choice } }));
        await new Promise((resolve) => window.setTimeout(resolve,
          payload.answer_feedback.phase === 'golden' && payload.answer_feedback.correct ? 850 : 280));
      }
      await refresh(boardSessionId);
    } catch (err) {
      try {
        const latest = await refresh(boardSessionId);
        const latestMatch = latest?.matches.find((match) => match.id === focusedMatch.id);
        if (latestMatch && ['active', 'golden'].includes(latestMatch.status)) {
          setError(err.message || 'Cevap kaydedilemedi.');
        }
      } catch {
        setError(err.message || 'Cevap kaydedilemedi.');
      }
    } finally {
      setAnswerFeedback((state) => ({ ...state, [side]: null }));
      setAnswerBusy((state) => ({ ...state, [side]: false }));
    }
  };

  if (loading) return <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: '#fffaf0' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fffaf0', color: ink, overflow: 'hidden',
      backgroundImage: 'radial-gradient(circle at 3% 96%, rgba(34,197,94,0.13) 0 60px, transparent 61px), radial-gradient(circle at 98% 96%, rgba(132,204,22,0.16) 0 74px, transparent 75px)' }}>
      <Box sx={{ maxWidth: 1720, mx: 'auto', px: { xs: 1.2, sm: 2, md: 3.2 }, pb: 4 }}>
        <MiniTaskHeader classCode={board?.class?.code} />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
          <Button component={Link} to={`/mini-tasks/${token}`} startIcon={<ArrowBackRoundedIcon />}
            sx={{ minHeight: 48, borderRadius: 99, px: 2, color: violet, fontSize: { xs: 17, md: 20 }, fontWeight: 900, textTransform: 'none',
              '&:hover': { bgcolor: '#eee9ff' },
              '&:focus-visible, &.Mui-focusVisible': { outline: '3px solid #4c1d95', outlineOffset: '3px' } }}>
            Mini Görevlere Dön
          </Button>
          {tournament?.status === 'active' && <Button variant="outlined" color="error" disabled={busy}
            onClick={() => setEndDialogOpen(true)} sx={{ minHeight: 44, borderRadius: 99, px: { xs: 1.5, md: 2.2 }, textTransform: 'none', fontWeight: 800,
              '&:focus-visible, &.Mui-focusVisible': { outline: '3px solid #4c1d95', outlineOffset: '3px' },
              fontSize: { xs: 13, md: 15 }, whiteSpace: 'nowrap' }}>
            Turnuvayı Sonlandır
          </Button>}
          {tournament?.status === 'completed' && !offlineActive && <Button variant="contained" disabled={busy}
            onClick={prepareNewTournament} sx={{ minHeight: 40, borderRadius: 99, px: { xs: 1.5, md: 2.2 }, textTransform: 'none', fontWeight: 900,
              fontSize: { xs: 13, md: 15 }, whiteSpace: 'nowrap', bgcolor: '#7c3aed', boxShadow: '0 3px 0 #4c1d95',
              '&:hover': { bgcolor: '#6d28d9' } }}>
            Yeni Turnuva
          </Button>}
        </Box>

        <Box sx={{ textAlign: 'center', mb: { xs: 2, md: 3 } }}>
          <Typography component="h1" sx={{ color: violet, fontSize: { xs: 36, md: 57 }, fontWeight: 1000, lineHeight: 1.1, textShadow: '0 3px 0 #fff' }}>
            🏆 Sınıf Turnuvası
          </Typography>
          {tournament && <Typography sx={{ mt: 0.8, color: '#5b4b8a', fontSize: { xs: 19, md: 26 }, fontWeight: 900 }}>
            {tournament.participant_count} öğrenci • {tournament.content_label || 'Çarpım Tablosu'}
          </Typography>}
        </Box>

        {error && <Box role="alert" sx={{ bgcolor: '#fff1f2', color: '#9f1239', border: '2px solid #fda4af', borderRadius: 3,
          p: 1.5, mb: 2, fontSize: 18, fontWeight: 800 }}>{error}</Box>}

        <Dialog open={endDialogOpen} onClose={() => !busy && setEndDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 1000, color: ink }}>Turnuva sonlandırılsın mı?</DialogTitle>
          <DialogContent>
            <Typography>Devam eden eşleşmeler kapanır. Kazanan ve sonuç kaydı oluşturulmaz.</Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <Button disabled={busy} onClick={() => setEndDialogOpen(false)} sx={{ textTransform: 'none', fontWeight: 800 }}>Vazgeç</Button>
            <Button variant="contained" color="error" disabled={busy} onClick={endTournament}
              sx={{ textTransform: 'none', fontWeight: 900 }}>Sonlandır</Button>
          </DialogActions>
        </Dialog>

        {tournament?.mode === 'local_tournament' && tournament.status === 'active' && !offlineActive && (
          <Box sx={{ maxWidth: 780, mx: 'auto', p: 4, textAlign: 'center', borderRadius: 5, bgcolor: '#fff', border: '3px solid #ddd6fe', boxShadow: cardShadow }}>
            <Typography sx={{ mb: 2, fontSize: { xs: 21, md: 28 }, fontWeight: 900 }}>
              {busy || loading ? 'Tüm sorular ve görseller hazırlanıyor...' : 'Tüm sorular ve görseller yüklenmeli.'}
            </Typography>
            {!busy && <Button variant="contained" onClick={() => run(async () => {
              installImageUrls(await preloadTournamentImages(tournament));
              setOfflineActive(true);
            })} sx={actionButton}>Soruları Yeniden Yükle</Button>}
          </Box>
        )}

        {tournament?.mode === 'local_tournament' && offlineActive && tournament.status === 'completed' && (
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography role="status" sx={{ color: violet, fontSize: 21, fontWeight: 900 }}>
              {submissionFailed ? 'Turnuva sonucu henüz kaydedilemedi.' : 'Turnuva sonucu kaydediliyor...'}
            </Typography>
            {submissionFailed && <Button variant="contained" onClick={submitOfflineTournament} sx={{ ...actionButton, mt: 2 }}>Tekrar Gönder</Button>}
          </Box>
        )}

        {!tournament && board && (
          <Box sx={{ maxWidth: 1180, mx: 'auto', p: { xs: 2, md: 3 }, borderRadius: 5, bgcolor: '#fff',
            border: '3px solid #d8cafe', boxShadow: cardShadow }}>
            <Typography component="h2" sx={{ fontSize: { xs: 25, md: 34 }, fontWeight: 1000, color: violet }}>Turnuva İçeriği</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.4, my: 2 }}>
              {[
                ['mixed', '🎲 Karışık', 'DANDU her eşleşmeye uygun görevi otomatik seçer.'],
                ['topic', '📚 Ders / Konu Seç', 'Turnuvanın konusunu sen belirle.'],
              ].map(([mode, title, description]) => (
                <Button key={mode} onClick={() => setContentMode(mode)} aria-pressed={contentMode === mode}
                  sx={{ display: 'block', textAlign: 'left', textTransform: 'none', p: { xs: 2, md: 2.5 },
                    borderRadius: 4, border: `3px solid ${contentMode === mode ? '#7c3aed' : '#ddd6fe'}`,
                    bgcolor: contentMode === mode ? '#f3e8ff' : '#fff', color: ink,
                    '&:hover': { bgcolor: '#f3e8ff' } }}>
                  <Typography sx={{ fontSize: { xs: 21, md: 25 }, fontWeight: 1000 }}>{title}</Typography>
                  <Typography sx={{ fontSize: { xs: 15, md: 18 }, fontWeight: 700, color: '#66598a' }}>{description}</Typography>
                </Button>
              ))}
            </Box>
            {contentMode === 'topic' && <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
              <Box component="select" aria-label="Ders" value={selectedLessonCode}
                onChange={(event) => {
                  const lesson = event.target.value;
                  setSelectedLessonCode(lesson);
                  setSelectedContentKey(contentTopics.find((topic) => topic.lesson_code === lesson)?.key || '');
                }} sx={{ flex: 1, minWidth: 210, p: 1.4, borderRadius: 3, border: '2px solid #c4b5fd',
                  color: ink, bgcolor: '#fff', fontSize: 19, fontWeight: 800 }}>
                {lessonOptions.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
              </Box>
              <Box component="select" aria-label="Konu" value={selectedContentKey}
                onChange={(event) => setSelectedContentKey(event.target.value)}
                sx={{ flex: 1, minWidth: 210, p: 1.4, borderRadius: 3, border: '2px solid #c4b5fd',
                  color: ink, bgcolor: '#fff', fontSize: 19, fontWeight: 800 }}>
                {contentTopics.filter((topic) => topic.lesson_code === selectedLessonCode)
                  .map((topic) => <option key={topic.key} value={topic.key}>{topic.label}</option>)}
              </Box>
            </Box>}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2, px: 1.5, py: 1.2,
              borderRadius: 3, bgcolor: '#eff6ff', border: '1px solid #bfdbfe', color: '#475569' }}>
              <InfoOutlinedIcon aria-hidden="true" sx={{ color: '#3b82f6', fontSize: 21, mt: 0.15, flexShrink: 0 }} />
              <Typography sx={{ fontSize: { xs: 15, md: 17 }, fontWeight: 600, lineHeight: 1.45 }}>
                Bugün devamsız olan öğrenciler seçili gelmez. Katılımcıları kontrol edip gerekirse seçimi değiştirebilirsiniz.
              </Typography>
            </Box>
            {!contentTopics.length && <Typography sx={{ color: '#b91c1c', fontWeight: 800, mb: 2 }}>
              Bu sınıf için uygun turnuva içeriği bulunamadı.
            </Typography>}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(5, minmax(0, 1fr))' },
              gap: 1.3, mb: 3 }}>
              {board.students.map((student) => {
                const selected = selectedIds.includes(Number(student.id));
                const theme = getStudentAvatar(student.id, student.avatar_id, student.starter_emoji);
                return (
                  <Box component="label" key={student.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.8, minWidth: 0, cursor: 'pointer',
                    p: 1, borderRadius: 3, bgcolor: selected ? theme.bgColor : '#f8fafc',
                    border: `2px solid ${selected ? theme.borderColor : '#e2e8f0'}`, opacity: selected ? 1 : 0.62 }}>
                    <Checkbox checked={selected} onChange={() => setSelectedIds((current) => current.includes(Number(student.id))
                      ? current.filter((id) => id !== Number(student.id)) : [...current, Number(student.id)])}
                      inputProps={{ 'aria-label': student.display_name }} sx={{ p: 0.3, color: violet, '&.Mui-checked': { color: violet } }} />
                    <Box sx={{ fontSize: 28, lineHeight: 1 }} aria-hidden="true">{theme.imageUrl
                      ? <Box component="img" src={theme.imageUrl} alt="" loading="lazy" decoding="async" sx={{ width: 32, height: 32, objectFit: 'contain' }} />
                      : theme.avatar}</Box>
                    <Typography sx={{ minWidth: 0, overflowWrap: 'anywhere', fontSize: { xs: 16, md: 19 }, fontWeight: 900, color: ink }}>{student.display_name}</Typography>
                  </Box>
                );
              })}
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ mb: 1.5, color: '#66598a', fontSize: { xs: 15, md: 18 }, fontWeight: 700 }}>
                {selectedIds.length} öğrenci • {contentLabel} • Seviye DANDU tarafından belirlenir
              </Typography>
              <Button variant="contained" disabled={busy || !contentTopics.length || selectedIds.length < 2
                || (contentMode === 'topic' && !selectedContentKey)} onClick={beginTournament} sx={actionButton}>
                {selectedIds.length} Öğrenciyle Fikstür Oluştur
              </Button>
            </Box>
          </Box>
        )}

        {tournament && focusedMatch && focusedMatch.status === 'completed'
          && !(tournament.mode === 'local_tournament' && tournament.status === 'completed' && offlineActive)
          && (tournament.mode !== 'local_tournament' || offlineActive || tournament.status === 'completed') && (
          <>
            {tournament.status === 'completed' ? (
              <ChampionCard champion={focusedMatch.winner} soundKey={`${tournament.id}:champion`} />
            ) : (
              <AdvanceCard winner={focusedMatch.winner} soundKey={`${tournament.id}:match:${focusedMatch.id}:advance`}
                soundPan={winnerSoundPan(focusedMatch, focusedMatch.winner)} />
            )}
            <Box sx={{ textAlign: 'center' }}><Button variant="contained" onClick={() => setFocusMatchId(null)} sx={actionButton}>Fikstüre Dön</Button></Box>
          </>
        )}

        {tournament && focusedMatch?.mode === 'local_batch' && !localFocused && (
          <Box sx={{ maxWidth: 780, mx: 'auto', p: 4, textAlign: 'center', borderRadius: 5, bgcolor: '#fff',
            border: '3px solid #ddd6fe', boxShadow: cardShadow }}>
            {preparing ? (
              <>
                <CircularProgress sx={{ color: violet, mb: 1 }} />
                <Typography sx={{ fontSize: { xs: 21, md: 28 }, fontWeight: 900 }}>Sorular ve görseller hazırlanıyor...</Typography>
              </>
            ) : (
              <Button variant="contained" onClick={() => run(() => prepareLocalMatch(focusedMatch, tournament.id))} sx={actionButton}>
                Soruları Yeniden Yükle
              </Button>
            )}
          </Box>
        )}

        {tournament && localFocused && focusedMatch.mode === 'local_batch' && localMatch.phase === 'completed' && (
          <AdvanceCard winner={focusedMatch[`student_${localMatch.winnerSide}`]}
            soundKey={`${tournament.id}:match:${focusedMatch.id}:advance`}
            soundPan={tournamentSoundPan[localMatch.winnerSide] || 0}>
            <Typography role="status" sx={{ mt: 2, color: violet, fontSize: { xs: 18, md: 23 }, fontWeight: 900 }}>
              {submissionFailed ? 'Sonuç henüz kaydedilemedi.' : 'Sonuç kaydediliyor...'}
            </Typography>
            {submissionFailed && <Button variant="contained" onClick={submitLocalResult} sx={{ ...actionButton, mt: 2 }}>Tekrar Gönder</Button>}
          </AdvanceCard>
        )}

        {tournament && displayMatch && ['active', 'golden'].includes(displayMatch.status)
          && (focusedMatch.mode !== 'local_tournament' || offlineActive)
          && (!['local_batch', 'local_tournament'].includes(focusedMatch.mode) || (localFocused && localMatch.phase !== 'completed')) && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', gap: { xs: 1, md: 2 }, mb: 2 }}>
              <Typography sx={{ color: violet, fontSize: { xs: 26, md: 35 }, fontWeight: 1000 }}>
                {displayMatch.status === 'golden' ? '⚡ Altın Soru' : roundTitle(displayMatch.round, lastRound)}
              </Typography>
              {displayMatch.activity?.label && <Typography sx={{ color: '#6d28d9', fontSize: { xs: 18, md: 24 }, fontWeight: 900 }}>
                {activityLabel(displayMatch.activity)}
              </Typography>}
              {displayMatch.status === 'active' && (
                <Typography aria-label="Kalan süre" sx={{
                  px: { xs: 1.4, md: 2 }, py: 0.1, borderRadius: 99,
                  bgcolor: remainingMs <= 10000 ? '#fff1f2' : '#eee9ff',
                  border: `2px solid ${remainingMs <= 10000 ? '#fda4af' : '#ddd6fe'}`,
                  fontSize: { xs: 35, md: remainingMs <= 5000 ? 57 : 49 }, lineHeight: 1.1,
                  fontWeight: 1000, color: remainingMs <= 10000 ? '#be123c' : '#6d28d9',
                  transition: 'color 180ms ease, font-size 180ms ease',
                }}>
                  {remainingMs <= 0 ? 'Süre doldu' : `${Math.ceil(remainingMs / 1000)} sn`}
                </Typography>
              )}
            </Box>
            {displayMatch.status === 'golden' && !goldenWinner && (
              <Typography sx={{ textAlign: 'center', mb: 2, color: '#7c3aed', fontSize: { xs: 18, md: 24 }, fontWeight: 900 }}>
                İlk doğru cevap kazanır
              </Typography>
            )}
            {goldenWinner && (
              <Typography role="status" sx={{ textAlign: 'center', mb: 2, color: '#a16207', fontSize: { xs: 25, md: 38 }, fontWeight: 1000 }}>
                🏆 {goldenWinner.display_name} kazandı!
              </Typography>
            )}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 3 } }}>
              <MatchSide student={displayMatch.student_a} question={answerFeedback.a?.question || displayMatch.question_a}
                imageUrls={imageUrls} offlineImages={['local_batch', 'local_tournament'].includes(focusedMatch.mode)}
                busy={answerBusy.a || (remainingMs <= 0 && displayMatch.status === 'active') || Boolean(goldenWinner)}
                progress={displayMatch.progress_a ?? 0} questionCount={displayMatch.question_count ?? 10}
                showProgress={displayMatch.status === 'active'} feedback={answerFeedback.a}
                onAnswer={(choice) => answer('a', choice)} />
              <MatchSide student={displayMatch.student_b} question={answerFeedback.b?.question || displayMatch.question_b}
                imageUrls={imageUrls} offlineImages={['local_batch', 'local_tournament'].includes(focusedMatch.mode)}
                busy={answerBusy.b || (remainingMs <= 0 && displayMatch.status === 'active') || Boolean(goldenWinner)}
                progress={displayMatch.progress_b ?? 0} questionCount={displayMatch.question_count ?? 10}
                showProgress={displayMatch.status === 'active'} feedback={answerFeedback.b}
                onAnswer={(choice) => answer('b', choice)} />
            </Box>
          </>
        )}

        {tournament && !focusedMatch && (tournament.mode !== 'local_tournament' || offlineActive || tournament.status === 'completed') && (
          <>
            {tournament.status === 'completed'
              && !(tournament.mode === 'local_tournament' && offlineActive) && (
              <ChampionCard champion={tournament.champion} soundKey={`${tournament.id}:champion`} />
            )}
            {tournament.status === 'active' && nextMatch && (
              <NextMatch match={nextMatch} onStart={beginMatch} busy={busy} lastRound={lastRound} />
            )}
            {tournament.status === 'active' && activeMatch && !nextMatch && (
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Button variant="contained" onClick={() => setFocusMatchId(activeMatch.id)} sx={actionButton}>Karşılaşmaya Devam Et</Button>
              </Box>
            )}
            <Bracket matches={matches} nextMatchId={nextMatch?.id} />
          </>
        )}
      </Box>
    </Box>
  );
}
